import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  RewardEventFormRule,
  RewardEventFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultRewardEvent } from "@factory/form/form-default-reward-event.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseRewardEventModel } from "@factory/model/query-model-reward-event";
import {
  useQyDeleteRewardEvent,
  useQyGetRewardEventById,
  useQyUpdateRewardEvent,
} from "@factory/query/reward-event.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditRewardEvent() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultRewardEvent();
  const navigate = useNavigate();
  const { mapUpdateRewardEvent, mapDeleteRewardEvent } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { rewardEventId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const [isAllLevel, setIsAllLevel] = useState(true);

  // QUERY DECLARATION
  // API GETBYID RewardEvent
  const handleGetApiSuccess = (data: GetResponseRewardEventModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("isPoints", Boolean(responseData?.is_points));
    setValue("isStamp", Boolean(responseData?.is_stamp));
    setValue("isLevel", Boolean(responseData?.is_level));
    setValue("denominationPoints", responseData?.denomination_points);
    setValue("points", responseData?.points);
    setValue("denominationStamp", responseData?.denomination_stamp);
    if (responseData?.level) {
      setValue("level", responseData?.level);
      setIsAllLevel(false);
    }
    setValue("stamp", responseData?.stamp);
    if (responseData?.start_date) {
      setValue(
        "startDate",
        formatDateForInput(responseData?.start_date) as any,
        undefined
      );
    }
    if (responseData?.end_date) {
      setValue(
        "endDate",
        formatDateForInput(responseData?.end_date) as any,
        undefined
      );
    }
  };
  const {
    data: rewardEventResponse,
    isLoading,
    isError: rewardEventError,
  } = useQyGetRewardEventById(rewardEventId || "", handleGetApiSuccess);
  const rewardEventData = rewardEventResponse?.data?.[0];

  // API UPDATE RewardEvent
  const handleUpdateApiSuccess = () => {
    showSuccess("RewardEvent updated successfully");
  };
  const { mutate: updateRewardEvent, isLoading: isEditLoading } =
    useQyUpdateRewardEvent(handleUpdateApiSuccess);

  // API DELETE RewardEvent
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("RewardEvent deleted successfully");
  };
  const { mutate: deleteRewardEvent } = useQyDeleteRewardEvent(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<RewardEventFormSchema>({
    defaultValues: getDefault(rewardEventData),
    resolver: zodResolver(RewardEventFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RewardEventFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };

    if (isAllLevel) {
      updatedForm.level = "";
    }

    if (!rewardEventData || !rewardEventData.code) {
      throw new Error("No rewardEvent data");
    }

    const formData = mapUpdateRewardEvent(updatedForm, rewardEventData.code);
    updateRewardEvent(formData);
  };
  const handleValidateError = (err: FieldErrors<RewardEventFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!rewardEventData || !rewardEventData.code) {
      throw new Error("No rewardEvent data found");
    }

    const form = mapDeleteRewardEvent(rewardEventData.code);
    deleteRewardEvent(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const allLevelChange = (allLevel: boolean) => {
    setIsAllLevel(allLevel);
  };

  return {
    rewardEventId,
    rewardEventData,
    isLoading,
    rewardEventError,
    formMethod,
    actions,
    isEditLoading,
    isAllLevel,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    allLevelChange,
  };
}
